import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FloatingImage({ item, index, total, onPop }) {
  const ref = useRef(null);
  const [pos] = useState(() => {
    const cols = Math.ceil(Math.sqrt(total));
    const row = Math.floor(index / cols);
    const col = index % cols;
    const cellW = 100 / cols;
    const cellH = 100 / Math.ceil(total / cols);
    return {
      x: col * cellW + Math.random() * cellW * 0.5,
      y: row * cellH + Math.random() * cellH * 0.4,
      rotation: (Math.random() - 0.5) * 20,
      scale: 0.85 + Math.random() * 0.3,
      speed: 0.3 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
    };
  });

  useEffect(() => {
    let frame;
    const animate = () => {
      if (!ref.current) return;
      const t = Date.now() / 1000;
      const floatY = Math.sin(t * pos.speed + pos.phase) * 12;
      const floatX = Math.cos(t * pos.speed * 0.7 + pos.phase) * 8;
      const rot = pos.rotation + Math.sin(t * pos.speed * 0.4 + pos.phase) * 3;
      ref.current.style.transform = `translate(${floatX}px, ${floatY}px) rotate(${rot}deg) scale(${pos.scale})`;
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [pos]);

  return (
    <div
      ref={ref}
      className="absolute cursor-pointer group"
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        willChange: "transform",
      }}
      data-testid={`portfolio-item-${item.id}`}
      onClick={() => onPop(item)}
    >
      <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-500">
        <img
          src={item.image}
          alt={item.title || "Art"}
          className="w-32 h-32 md:w-48 md:h-48 object-cover transition-transform duration-500 group-hover:scale-110"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/10 pointer-events-none rounded-xl" />
      </div>
      {item.title && (
        <p
          className="text-center text-xs text-[#3a3a3a] mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ fontFamily: "'Lazy Dog', cursive", fontSize: "0.9rem" }}
        >
          {item.title}
        </p>
      )}
    </div>
  );
}

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [poppedItem, setPoppedItem] = useState(null);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/portfolio`).then((res) => setItems(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      if (!containerRef.current || poppedItem) return;
      const rect = containerRef.current.getBoundingClientRect();
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 15,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, [poppedItem]);

  const handlePop = useCallback((item) => {
    setPoppedItem(item);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setPoppedItem(null), 4000);
  }, []);

  return (
    <div data-testid="portfolio-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="py-8 md:py-10">
          <p
            className="text-xs tracking-[0.3em] text-[#E01A23] mb-2"
            style={{ fontFamily: "'Courier New', Courier, monospace" }}
          >
            the gallery
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-[#1A1A1A]"
            style={{ fontFamily: "'Lazy Dog', cursive" }}
          >
            portfolio
          </h1>
        </div>
      </div>

      {/* ─── Pop-up Overlay with Swirl + Stars ─── */}
      {poppedItem && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          onClick={() => setPoppedItem(null)}
          data-testid="portfolio-popup"
        >
          {/* Dark backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" style={{ animation: "fadeIn 0.3s ease-out" }} />

          {/* Shining stars scattered around */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute z-20 pointer-events-none"
              style={{
                left: `${15 + Math.random() * 70}%`,
                top: `${10 + Math.random() * 80}%`,
                animation: `starTwinkle 1.5s ease-in-out ${i * 0.12}s infinite`,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.5 9.5L21 12L13.5 14.5L12 22L10.5 14.5L3 12L10.5 9.5L12 2Z" fill="white" opacity="0.9" />
              </svg>
            </div>
          ))}

          {/* Floating stars rising up */}
          {[...Array(8)].map((_, i) => (
            <div
              key={`float-${i}`}
              className="absolute z-20 pointer-events-none"
              style={{
                left: `${25 + Math.random() * 50}%`,
                bottom: `${20 + Math.random() * 30}%`,
                animation: `starFloat 2s ease-out ${i * 0.2}s infinite`,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.5 9.5L21 12L13.5 14.5L12 22L10.5 14.5L3 12L10.5 9.5L12 2Z" fill="#F5E6A0" opacity="0.8" />
              </svg>
            </div>
          ))}

          {/* Image with swirl-in */}
          <div className="relative z-10" style={{ animation: "swirlIn 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards" }}>
            {/* Shimmer ring */}
            <div
              className="absolute -inset-3 rounded-3xl opacity-60"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                backgroundSize: "200% 100%",
                animation: "shimmer 2s linear infinite",
              }}
            />
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/30 relative" style={{ width: "min(85vw, 550px)", height: "min(85vw, 550px)" }}>
              <img
                src={poppedItem.image}
                alt={poppedItem.title || "Art"}
                className="w-full h-full object-cover"
              />
            </div>
            {poppedItem.title && (
              <p
                className="text-center text-white text-lg mt-4"
                style={{ fontFamily: "'Lazy Dog', cursive", animation: "fadeIn 0.5s ease-out 0.5s both" }}
              >
                {poppedItem.title}
              </p>
            )}
          </div>
        </div>
      )}

      {items.length > 0 ? (
        <div
          ref={containerRef}
          className="relative w-full overflow-hidden"
          style={{ height: "max(70vh, 500px)", perspective: "1000px" }}
          data-testid="portfolio-collage"
        >
          <div
            className="absolute inset-0 transition-transform duration-700 ease-out"
            style={{
              transform: `translate(${mousePos.x}px, ${mousePos.y}px)`,
            }}
          >
            {items.map((item, index) => (
              <FloatingImage
                key={item.id}
                item={item}
                index={index}
                total={items.length}
                onPop={handlePop}
              />
            ))}
          </div>

          {/* Vignette */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: "radial-gradient(ellipse at center, transparent 40%, rgba(245,230,160,0.6) 100%)"
          }} />
        </div>
      ) : (
        <div className="text-center py-32">
          <p className="text-[#555] text-lg" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            no artwork in the gallery yet — check back soon
          </p>
        </div>
      )}

      <Footer />
    </div>
  );
}
